/**
 * RevenuesPrint — impression du rapport Performance « Revenues ».
 * Réplique RevenuesPage : KPIs (billed / billable / eff. rate vs PY), bridges
 * (PY→CY volume/price/mix/scope + std comparison) et tables By pool / By
 * employee. Réutilise buildPersons / aggregateByPool / computeBridge /
 * computeStdBridge → mêmes chiffres que l'écran. Couleurs alignées
 * (rev-pos #3F7E1F / rev-neg #C2543C).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { getChrYearly } from '../../api/chrYearly';
import { listCostPools } from '../../api/tdabc/cost_pools';
import { buildPersons, aggregateByPool, computeBridge, computeStdBridge } from './RevenuesPage';
import { formatNumber } from '../../utils/format';
import PrintReportHeader from '../hr/print/PrintReportHeader';
import '../hr/print/PeoplePrint.css';
import './RevenuesPage.css';

const NEG = '#C2543C';
const chf  = (v) => (v == null ? '—' : formatNumber(v, 0));
const rate = (v) => (v == null ? '—' : formatNumber(v, 2));
const pct  = (v) => (v == null || !isFinite(v) ? '—' : `${(v * 100).toLocaleString('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`);
const signed = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${formatNumber(v, 0)}`);
const signColor = (v) => (v > 0 ? '#3F7E1F' : v < 0 ? NEG : undefined);
// Classe de signe identique à l'écran (rev-pos / rev-neg).
const sign = (v) => (v > 0 ? 'rev-pos' : v < 0 ? 'rev-neg' : '');

// Bridge visuel (chips) — markup identique à RevenuesPage.BridgeRow.
function BridgeRow({ b }) {
  const steps = [{ k: 'Volume', v: b.volume }, { k: 'Price', v: b.price }, { k: 'Mix', v: b.mix }];
  if (Math.abs(b.scope) > 0.5) steps.push({ k: 'Scope', v: b.scope });
  return (
    <div className="rev-bridge-row">
      <span className="rev-bridge-step rev-bridge-anchor">
        <span className="rev-bridge-k">Revenue PY</span>
        <span className="rev-bridge-v">{chf(b.rPy)}</span>
      </span>
      {steps.map(s => (
        <span key={s.k} className="rev-bridge-step">
          <span className="rev-bridge-k">{s.k}</span>
          <span className={`rev-bridge-v ${sign(s.v)}`}>{signed(s.v)}</span>
        </span>
      ))}
      <span className="rev-bridge-step rev-bridge-anchor">
        <span className="rev-bridge-k">Revenue CY</span>
        <span className="rev-bridge-v">{chf(b.rCy)}</span>
      </span>
    </div>
  );
}

export default function RevenuesPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const py = year - 1;
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!tenantId) return undefined;
    Promise.all([
      getChrYearly(tenantId, year).then(r => { if (r?.error) throw new Error(r.error); return r.data || r; }),
      getChrYearly(tenantId, py).then(r => (r?.error ? { rows: [] } : (r.data || r))).catch(() => ({ rows: [] })),
      listCostPools(tenantId),
    ]).then(([cy, pyData, cp]) => {
      if (!alive) return;
      setData({ cy, pyData, costPools: (cp && cp.data) || cp || [] });
      if (onDataReady) onDataReady('revenues');
    }).catch(() => { if (onDataReady) onDataReady('revenues'); });
    return () => { alive = false; };
  }, [tenantId, year]); // eslint-disable-line

  const model = useMemo(() => {
    if (!data) return null;
    const overheadIds = new Set((data.costPools || []).filter(p => p.is_overhead_pool).map(p => p.id));
    const pCy = buildPersons(data.cy, overheadIds);
    const pPy = buildPersons(data.pyData, overheadIds);
    const poolsCy = aggregateByPool(pCy);
    const poolsPy = aggregateByPool(pPy);
    const pyPoolByKey = new Map(poolsPy.map(p => [p.key, p]));
    const pyPersonByKey = new Map(pPy.map(p => [p.key, p]));
    const bridgePerson = computeBridge(
      pCy.map(p => ({ key: p.key, h: p.billable, r: p.billed })),
      pPy.map(p => ({ key: p.key, h: p.billable, r: p.billed })),
    );
    const bridgePool = computeBridge(
      poolsCy.map(p => ({ key: p.key, h: p.h, r: p.r })),
      poolsPy.map(p => ({ key: p.key, h: p.h, r: p.r })),
    );
    const stdBridge = computeStdBridge(pCy);

    const sum = (arr, k) => arr.reduce((s, x) => s + (Number(x[k]) || 0), 0);
    const rCy = sum(poolsCy, 'r'), hCy = sum(poolsCy, 'h');
    const rPy = bridgePool.rPy, hPy = sum(poolsPy, 'h');
    const effCy = hCy > 0 ? rCy / hCy : null;
    const effPy = hPy > 0 ? rPy / hPy : null;
    const dRevPct = rPy ? (rCy - rPy) / rPy : null;
    const stdTotRate = hCy > 0 ? sum(poolsCy, 'stdRev') / hCy : null;

    const poolRows = poolsCy.map(p => {
      const prev = pyPoolByKey.get(p.key) || { h: 0, r: 0 };
      return {
        ...p, prevH: prev.h, prevR: prev.r,
        eCy: p.h > 0 ? p.r / p.h : null,
        ePy: prev.h > 0 ? prev.r / prev.h : null,
        d: prev.r ? (p.r - prev.r) / prev.r : null,
        std: p.h > 0 ? p.stdRev / p.h : null,
      };
    }).sort((a, b) => b.r - a.r);

    const personRows = pCy.map(p => {
      const prev = pyPersonByKey.get(p.key) || { billable: 0, billed: 0, hasBilled: false };
      return {
        ...p, prevBillable: prev.billable, prevBilled: prev.billed, prevHasBilled: prev.hasBilled,
        eCy: p.billable > 0 ? p.billed / p.billable : null,
        ePy: prev.billable > 0 ? prev.billed / prev.billable : null,
      };
    }).sort((a, b) => b.billed - a.billed);

    return { pCy, bridgePerson, stdBridge, rCy, hCy, rPy, hPy, effCy, effPy, dRevPct, stdTotRate, poolRows, personRows };
  }, [data]);

  if (!model) return <div className="ppl-print__section"><p>{t('common:status.loading')}</p></div>;

  const { pCy, bridgePerson, stdBridge, rCy, hCy, rPy, hPy, effCy, effPy, dRevPct, stdTotRate, poolRows, personRows } = model;

  return (
    <>
      {/* ── Page 1 : Summary + bridges + By pool ── */}
      <section className="ppl-print__section ppl-print__section--wide">
        <PrintReportHeader category="Performance" title={`Revenues — ${year} vs ${py}`} year={year} tenant={activeTenant} editedAt={editedAt} />

        {/* ── Synthèse visuelle (cartes KPI + bridges) — identique à l'écran ── */}
        <div className="rev-synth" style={{ marginBottom: 14 }}>
          <div className="rev-synth__head">Revenues · {year} · {pCy.length} persons</div>
          <div className="rev-synth__row">
            <div className="rev-synth__item">
              <span className="rev-synth__label">Total billed</span>
              <span className="rev-synth__value">{chf(rCy)} <small>CHF</small></span>
              <span className={`rev-synth__sub ${sign(rCy - rPy)}`}>{signed(rCy - rPy)} vs {py} · {pct(dRevPct)}</span>
            </div>
            <div className="rev-synth__item">
              <span className="rev-synth__label">Billable hours</span>
              <span className="rev-synth__value">{chf(hCy)} <small>h</small></span>
              <span className={`rev-synth__sub ${sign(hCy - hPy)}`}>{signed(hCy - hPy)} vs {py}</span>
            </div>
            <div className="rev-synth__item">
              <span className="rev-synth__label">Effective rate</span>
              <span className="rev-synth__value">{rate(effCy)} <small>CHF/h</small></span>
              <span className={`rev-synth__sub ${sign((effCy || 0) - (effPy || 0))}`}>
                {effPy != null ? `${signed(effCy - effPy)} vs ${py}` : 'prior n/a'}
              </span>
            </div>
          </div>

          {(bridgePerson.rPy > 0 || stdBridge) && (
            <div className="rev-synth__analysis">
              <div className="rev-analysis-cols">
                {bridgePerson.rPy > 0 && (
                  <div className="rev-analysis-block rev-analysis-block--py">
                    <div className="rev-bridge-sub">Prior year analysis · {py} → {year}</div>
                    <BridgeRow b={bridgePerson} />
                  </div>
                )}
                {stdBridge && (
                  <div className="rev-analysis-block">
                      <div className="rev-bridge-sub">Standard comparison · {year} ({stdBridge.n} {p('avec std')})</div>
                    <div className="rev-bridge-row">
                      <span className="rev-bridge-step rev-bridge-anchor">
                        <span className="rev-bridge-k">Rev @ STD</span>
                        <span className="rev-bridge-v">{chf(stdBridge.stdRev)}</span>
                      </span>
                      <span className="rev-bridge-step">
                        <span className="rev-bridge-k">Price</span>
                        <span className={`rev-bridge-v ${sign(stdBridge.price)}`}>{signed(stdBridge.price)}</span>
                      </span>
                      <span className="rev-bridge-step rev-bridge-anchor">
                        <span className="rev-bridge-k">Billed {year}</span>
                        <span className="rev-bridge-v">{chf(stdBridge.billed)}</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <p className="rev-bridge-note">
                Bridge PY→CY : Volume = Δh × tarif PY · Price = Σh × Δtarif · Mix = bascule entre employés · Scope = arrivées − départs.
                Réalisé vs Std : Rev @ STD = Σ std × billable · Price = réalisation vs prix standard.
              </p>
            </div>
          )}
        </div>

        {/* By pool */}
        <table className="ppl-print__table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Pool</th>
              <th>Std price</th>
              <th>Billable h {py}</th><th>Billable h {year}</th>
              <th>Billed {py}</th><th>Billed {year}</th>
              <th>Δ %</th>
              <th>Eff. rate {py}</th><th>Eff. rate {year}</th>
            </tr>
          </thead>
          <tbody>
            {poolRows.map(p => (
              <tr key={p.key}>
                <td style={{ textAlign: 'left' }}>{p.name}</td>
                <td>{rate(p.std)}</td>
                <td>{chf(p.prevH)}</td>
                <td>{chf(p.h)}</td>
                <td>{chf(p.prevR)}</td>
                <td><strong>{chf(p.r)}</strong></td>
                <td style={{ color: signColor(p.d) }}>{pct(p.d)}</td>
                <td>{rate(p.ePy)}</td>
                <td style={{ color: NEG, fontWeight: 600 }}>{rate(p.eCy)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ textAlign: 'left' }}><strong>TOTAL</strong></td>
              <td><strong>{rate(stdTotRate)}</strong></td>
              <td><strong>{chf(hPy)}</strong></td>
              <td><strong>{chf(hCy)}</strong></td>
              <td><strong>{chf(rPy)}</strong></td>
              <td><strong>{chf(rCy)}</strong></td>
              <td style={{ color: signColor(dRevPct) }}><strong>{pct(dRevPct)}</strong></td>
              <td><strong>{rate(effPy)}</strong></td>
              <td style={{ color: NEG }}><strong>{rate(effCy)}</strong></td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* ── Page 2 : By employee ── */}
      <section className="ppl-print__section ppl-print__section--wide">
        <PrintReportHeader category="Performance" title={`Revenues — By employee · ${year}`} year={year} tenant={activeTenant} editedAt={editedAt} />
        <table className="ppl-print__table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Person</th>
              <th style={{ textAlign: 'left' }}>Pool</th>
              <th>Std price</th>
              <th>Billable h {py}</th><th>Billable h {year}</th>
              <th>Billed {py}</th><th>Billed {year}</th>
              <th>Eff. rate {py}</th><th>Eff. rate {year}</th>
            </tr>
          </thead>
          <tbody>
            {personRows.map(p => (
              <tr key={p.key}>
                <td style={{ textAlign: 'left' }}>{p.name}</td>
                <td style={{ textAlign: 'left' }}>{p.poolName}</td>
                <td>{p.hasStd ? rate(p.stdPrice) : '—'}</td>
                <td>{chf(p.prevBillable)}</td>
                <td>{chf(p.billable)}</td>
                <td>{p.prevHasBilled ? chf(p.prevBilled) : '—'}</td>
                <td><strong>{p.hasBilled ? chf(p.billed) : '—'}</strong></td>
                <td>{rate(p.ePy)}</td>
                <td style={{ color: NEG, fontWeight: 600 }}>{rate(p.eCy)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ textAlign: 'left' }} colSpan={2}><strong>Total ({pCy.length})</strong></td>
              <td />
              <td><strong>{chf(hPy)}</strong></td>
              <td><strong>{chf(hCy)}</strong></td>
              <td><strong>{chf(rPy)}</strong></td>
              <td><strong>{chf(rCy)}</strong></td>
              <td><strong>{rate(effPy)}</strong></td>
              <td style={{ color: NEG }}><strong>{rate(effCy)}</strong></td>
            </tr>
          </tbody>
        </table>
      </section>
    </>
  );
}
